import type { ParseKeys, TFunction } from "i18next";

import type { IStatusColor } from "@components/atoms/statusChipBase";
import type { INoShowTypes } from "@/api/appointment";
import type { IMspVariants } from "@components/organisms/appointmentCard/components/appointmentCardBottom";

/** Type representing possible appointment status values. */
export type IAppointmentStatus =
  | "pending"
  | "accepted"
  | "declined"
  | "in_session"
  | "pending_summary_note"
  | "completed"
  | "canceled"
  | "no_show";

export type IAppointmentDataStatus = "performed" | "un-performed";

/** Props for an appointment status component. */
export interface IAppointmentStatusProps {
  /** The status of the appointment. */
  status: IAppointmentStatus;
  isCard?: boolean;
  noShowCause?: INoShowTypes;
  noShowReason?: string;
  isLoading?: boolean;
}

export interface IAppointmentDataStatusProps {
  /** The status of the appointment. */
  status: IAppointmentDataStatus;
  isMsp?: boolean;
  mspType?: IMspVariants;
}

/** Type representing data for different appointment status values. */
export type IAppointmentStatusData = Record<
  IAppointmentStatus,
  {
    /** The color of the status chip associated with this status. */
    color: IStatusColor;
    /** The label text for the status. */
    label: string;
  }
>;

/** Type representing data for different appointment status values. */
export type IAppointmentDataStatusData = Record<
  IAppointmentDataStatus,
  {
    /** The color of the status chip associated with this status. */
    color: IStatusColor;
    /** The label text for the status. */
    label: string;
  }
>;

/** Data array for appointment status options. */
export const appointmentStatusData = (
  t: TFunction<"common">,
): IAppointmentStatusData => ({
  pending: { label: t("pending"), color: "warning" },
  accepted: { label: t("confirmed"), color: "success" },
  declined: { label: t("denied"), color: "danger" },
  in_session: { label: t("inProgress"), color: "primary" },
  pending_summary_note: {
    label: t("pendingWriteSummaryNote"),
    color: "warning",
  },
  completed: { label: t("finished"), color: "success" },
  no_show: { label: t("unfinished"), color: "danger" },
  canceled: { label: t("canceled"), color: "danger" },
});

export const appointmentDataStatusData = (
  t: TFunction<"common">,
  isMsp: boolean,
  mspType?: IMspVariants,
): IAppointmentDataStatusData => ({
  performed: {
    color: "success",
    label: t(isMsp ? getMspTypePerformedLabel(mspType) : "executed"),
  },
  "un-performed": {
    color: "danger",
    label: t(isMsp ? getMspTypeUnPerformedLabel(mspType) : "notExecuted"),
  },
});

const getMspTypePerformedLabel = (
  mspType?: IMspVariants,
): ParseKeys<"common"> => {
  if (mspType === "msp_image") {
    return "mspImagesPerformed";
  }
  if (mspType === "msp_order") {
    return "mspExperimentPerformed";
  }
  return "mspPerformed";
};

const getMspTypeUnPerformedLabel = (
  mspType?: IMspVariants,
): ParseKeys<"common"> => {
  if (mspType === "msp_image") {
    return "mspImagesUnPerformed";
  }
  if (mspType === "msp_order") {
    return "mspExperimentUnPerformed";
  }
  return "mspUnPerformed";
};
